if (!customElements.get('glow-in-hand')) {
  customElements.define(
    'glow-in-hand',
    class GlowInHand extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
        this.toggle = this.querySelector('[data-toggle]');
        this.progress = this.querySelector('.glow-in-hand__progress span');
        this.now = this.querySelector('.glow-in-hand__now');
        this.nowIndex = this.querySelector('[data-now-index]');
        this.nowLabel = this.querySelector('[data-now-label]');
        this.nowText = this.querySelector('[data-now-text]');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.userPaused = false;
        this.live = -1;

        // Flatten "2.4, 6.4" style cue times into one sorted timeline.
        this.timeline = [];
        this.tabs.forEach((tab, index) => {
          (tab.dataset.times || '')
            .split(',')
            .map((t) => parseFloat(t))
            .filter((t) => !Number.isNaN(t))
            .forEach((t) => this.timeline.push({ t, index }));
          tab.addEventListener('click', () => this.select(index, true));
          tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
        });
        this.timeline.sort((a, b) => a.t - b.t);

        if (!this.video) return;

        this.tick = this.tick.bind(this);
        this.video.addEventListener('play', () => {
          this.classList.remove('is-paused');
          this.updateToggle();
          requestAnimationFrame(this.tick);
        });
        this.video.addEventListener('pause', () => {
          this.classList.add('is-paused');
          this.updateToggle();
        });
        this.video.addEventListener('timeupdate', () => this.sync());

        if (this.toggle) {
          this.toggle.addEventListener('click', () => {
            if (this.video.paused) {
              this.userPaused = false;
              this.play();
            } else {
              this.userPaused = true;
              this.video.pause();
            }
          });
        }

        this.classList.add('is-paused');
        this.updateToggle();

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting && !this.userPaused && !this.reducedMotion) this.play();
                if (!entry.isIntersecting && !this.video.paused) this.video.pause();
              });
            },
            { threshold: 0.35 }
          );
          this.observer.observe(this.video);
        } else if (!this.reducedMotion) {
          this.play();
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      onKeydown(event, index) {
        const last = this.tabs.length - 1;
        let next = null;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = last;
        if (next === null) return;
        event.preventDefault();
        this.select(next, true);
        this.tabs[next].focus();
      }

      select(index, seek) {
        this.tabs.forEach((tab, i) => {
          const on = i === index;
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.tabIndex = on ? 0 : -1;
          if (this.panels[i]) this.panels[i].hidden = !on;
        });
        if (!seek || !this.video) return;
        const hit = this.timeline.find((entry) => entry.index === index);
        if (hit) {
          this.video.currentTime = hit.t + 0.05;
          this.sync();
        }
      }

      play() {
        const attempt = this.video.play();
        if (attempt && attempt.catch) attempt.catch(() => this.classList.add('is-paused'));
      }

      tick() {
        this.sync();
        if (this.video && !this.video.paused) requestAnimationFrame(this.tick);
      }

      sync() {
        const time = this.video.currentTime;
        const duration = this.video.duration || 1;
        let current = null;
        for (const entry of this.timeline) {
          if (entry.t <= time) current = entry;
          else break;
        }
        const live = current ? current.index : -1;
        if (live !== this.live) {
          this.live = live;
          this.tabs.forEach((tab, i) => tab.classList.toggle('is-live', i === live));
          this.showNow(live);
        }
        if (this.progress) this.progress.style.transform = `scaleX(${Math.min(time / duration, 1)})`;
      }

      /* Caption on the video: the mode that is on the device display right now */
      showNow(index) {
        if (!this.now) return;
        const tab = this.tabs[index];
        this.now.classList.toggle('is-on', Boolean(tab));
        if (!tab) return;
        const text = (selector) => (tab.querySelector(selector)?.textContent || '').trim();
        this.nowIndex.textContent = text('.glow-in-hand__tab-index');
        this.nowLabel.textContent = text('.glow-in-hand__tab-label');
        this.nowText.textContent = text('.glow-in-hand__tab-text');
        /* Restart the caption's entrance so each mode change reads as a cut */
        this.now.classList.remove('is-cut');
        void this.now.offsetWidth;
        this.now.classList.add('is-cut');
      }

      updateToggle() {
        if (!this.toggle) return;
        const paused = !this.video || this.video.paused;
        this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
      }
    }
  );
}
